import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Container, Engine, ISourceOptions } from '@tsparticles/engine';
import { loadSlim } from '@tsparticles/slim';
import { ButtonModule } from '@syncfusion/ej2-angular-buttons';
import { SkeletonModule } from '@syncfusion/ej2-angular-notifications';
import { NgxParticlesModule } from '@tsparticles/angular';
import { Subscription, catchError, finalize, of } from 'rxjs';
import { environment } from '../../environments/environment';
import { RequestErrorPolicyService } from '../error/request-error-policy.service';
import { NavbarActiveElectionService } from 'src/app/core/navbar/navbar-active-election.service';
import { CipaElectionVotingExperienceDto } from './voting.models';
import { VotingLiveTrackerComponent } from './voting-live-tracker.component';
import { VotingNoActiveElectionComponent } from './voting-no-active-election.component';
import { VotingStepsComponent } from './voting-steps.component';
import { VotingService } from './voting.service';
import { AuthUserService } from '../init/auth-user.service';

@Component({
  selector: 'app-voting',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    SkeletonModule,
    NgxParticlesModule,
    VotingLiveTrackerComponent,
    VotingNoActiveElectionComponent,
    VotingStepsComponent,
  ],
  templateUrl: './voting.component.html',
  styleUrls: ['./voting.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VotingComponent implements AfterViewInit, OnDestroy, OnInit {
  particlesId = 'voting-hero-particles';
  showParticles = true;

  isLoading = true;
  showLoadingSkeleton = false;
  votingData: CipaElectionVotingExperienceDto | null = null;
  errorMessage = '';

  private particlesWatchdogId?: number;
  private loadingSkeletonTimerId?: number;
  private lastParticlesRemountAt = 0;
  private particlesContainer?: Container;
  private readonly subs = new Subscription();
  private hasLoadedRouteContext = false;
  private currentExperienceContext: VotingExperienceContext | null = null;

  particlesOptions: ISourceOptions = {
    fullScreen: {
      enable: false,
    },
    background: {
      color: 'transparent',
    },
    fpsLimit: 60,
    detectRetina: true,
    particles: {
      number: {
        value: 120,
        density: {
          enable: true,
          width: 1920,
          height: 520,
        },
      },
      color: {
        value: ['#d6ff9e', '#e8ffe2', '#9aee74'],
      },
      links: {
        enable: true,
        distance: 140,
        color: '#d4ffbf',
        opacity: 0.26,
        width: 1,
      },
      move: {
        enable: true,
        speed: 0.6,
        direction: 'none',
        outModes: {
          default: 'out',
        },
      },
      opacity: {
        value: {
          min: 0.18,
          max: 0.48,
        },
      },
      size: {
        value: {
          min: 1,
          max: 3.5,
        },
      },
      shape: {
        type: 'circle',
      },
    },
    interactivity: {
      events: {
        onHover: {
          enable: true,
          mode: 'grab',
        },
      },
      modes: {
        grab: {
          distance: 120,
          links: {
            opacity: 0.2,
          },
        },
      },
    },
  };

  constructor(
    private readonly route: ActivatedRoute,
    private readonly votingService: VotingService,
    private readonly requestErrorPolicy: RequestErrorPolicyService,
    private readonly auth: AuthUserService,
    private readonly navbarActiveElectionService: NavbarActiveElectionService,
    private readonly cdr: ChangeDetectorRef,
  ) { }

  get maxVotesPerVoter(): number {
    return this.votingData?.election.maxVotesPerVoter ?? 0;
  }

  get shouldShowLiveTracking(): boolean {
    if (this.votingData?.election?.isVotingPendingStart || !this.votingData?.liveTracking) {
      return false;
    }

    if (this.votingData.election.status === 'votacao') {
      return this.votingData.voter.canVote === false;
    }

    return this.votingData.election.status === 'encerrada' ||
      this.votingData.election.status === 'votacao_encerrada' ||
      this.votingData.election.status === 'homologada';
  }

  get shouldShowVotingShell(): boolean {
    if (!this.votingData || this.votingData.election.isVotingPendingStart) {
      return false;
    }

    if (this.shouldShowLiveTracking) {
      return true;
    }

    return this.votingData.election.status === 'votacao';
  }

  get shouldShowNoActiveElection(): boolean {
    return !this.isLoading && !this.errorMessage && !this.shouldShowVotingShell;
  }

  particlesInit = async (engine: Engine): Promise<void> => {
    await loadSlim(engine);
  };

  particlesLoaded(container: Container): void {
    this.particlesContainer = container;
  }

  ngOnInit(): void {
    this.subs.add(
      this.route.paramMap.subscribe((paramMap) => {
        const routeContext = this.resolveRouteExperienceContext(
          paramMap.get('companySuffix'),
          paramMap.get('electionGuid'),
        );
        const authContext = this.resolveAuthExperienceContext();
        const nextContext = routeContext ?? authContext;

        if (!nextContext) {
          this.hasLoadedRouteContext = true;
          this.currentExperienceContext = null;
          this.errorMessage = 'Não foi possível identificar a eleição para carregar a experiência de votação.';
          this.votingData = null;
          this.isLoading = false;
          this.showLoadingSkeleton = false;
          this.clearLoadingSkeletonTimer();
          this.cdr.markForCheck();
          return;
        }

        if (this.hasLoadedRouteContext && this.isSameExperienceContext(nextContext, this.currentExperienceContext)) {
          return;
        }

        this.hasLoadedRouteContext = true;
        this.currentExperienceContext = nextContext;
        this.errorMessage = '';
        this.votingData = null;

        this.loadVotingExperience(nextContext);
      }),
    );
  }

  ngAfterViewInit(): void {
    if (environment.production || typeof window === 'undefined') {
      return;
    }

    this.particlesWatchdogId = window.setInterval(() => {
      if (!this.showParticles) {
        return;
      }

      const hostElement = document.getElementById(this.particlesId);
      const hasCanvas = !!hostElement?.querySelector('canvas');

      if (!hasCanvas && Date.now() - this.lastParticlesRemountAt > 1000) {
        this.remountParticles();
      }
    }, 1000);
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    this.clearLoadingSkeletonTimer();

    if (this.particlesWatchdogId !== undefined && typeof window !== 'undefined') {
      window.clearInterval(this.particlesWatchdogId);
    }

    this.particlesContainer?.destroy();
  }

  handleActiveElectionDetected(): void {
    if (this.isLoading) {
      return;
    }

    if (!this.currentExperienceContext) {
      return;
    }

    this.navbarActiveElectionService.notifyActiveElectionDetected();
    this.errorMessage = '';
    this.votingData = null;
    this.loadVotingExperience(this.currentExperienceContext);
  }

  handleResetRequested(): void {
    if (this.isLoading || !this.currentExperienceContext) {
      return;
    }

    this.errorMessage = '';
    this.votingData = null;
    this.loadVotingExperience(this.currentExperienceContext);
  }

  private loadVotingExperience(context: VotingExperienceContext): void {
    this.isLoading = true;
    this.showLoadingSkeleton = false;
    this.scheduleLoadingSkeleton();

    this.votingService.getVotingExperience(context.companySuffix, context.electionGuid).pipe(
      catchError((error) => {
        this.errorMessage = this.requestErrorPolicy.resolveNonHttpMessage(
          error,
          'Não foi possível carregar a votação no momento. Tente novamente em instantes.',
        );

        return of(null);
      }),
      finalize(() => {
        this.clearLoadingSkeletonTimer();
        this.showLoadingSkeleton = false;
        this.isLoading = false;
        this.cdr.markForCheck();
      }),
    ).subscribe((data) => {
      if (!data) {
        return;
      }

      this.votingData = data;
      this.cdr.markForCheck();
    });
  }

  private resolveRouteExperienceContext(
    routeCompanySuffix: string | null,
    routeElectionGuid: string | null,
  ): VotingExperienceContext | null {
    const companySuffix = routeCompanySuffix?.trim() ?? '';
    const electionGuid = routeElectionGuid?.trim() ?? '';

    if (!companySuffix || !this.isGuid(electionGuid)) {
      return null;
    }

    return {
      companySuffix,
      electionGuid,
    };
  }

  private resolveAuthExperienceContext(): VotingExperienceContext | null {
    const currentUser = this.auth.getCurrentUser();
    const companySuffix = currentUser.companySuffix?.trim() ?? '';
    const electionGuid = currentUser.electionGuid?.trim() ?? '';

    if (!companySuffix || !this.isGuid(electionGuid)) {
      return null;
    }

    return {
      companySuffix,
      electionGuid,
    };
  }

  private isSameExperienceContext(
    left: VotingExperienceContext | null,
    right: VotingExperienceContext | null,
  ): boolean {
    return left?.companySuffix === right?.companySuffix &&
      left?.electionGuid.toLowerCase() === right?.electionGuid.toLowerCase();
  }

  private isGuid(value: string): boolean {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
  }

  private scheduleLoadingSkeleton(): void {
    this.clearLoadingSkeletonTimer();

    if (typeof window === 'undefined') {
      this.showLoadingSkeleton = true;
      return;
    }

    this.loadingSkeletonTimerId = window.setTimeout(() => {
      this.loadingSkeletonTimerId = undefined;

      if (!this.isLoading) {
        return;
      }

      this.showLoadingSkeleton = true;
      this.cdr.markForCheck();
    }, 220);
  }

  private clearLoadingSkeletonTimer(): void {
    if (this.loadingSkeletonTimerId === undefined || typeof window === 'undefined') {
      return;
    }

    window.clearTimeout(this.loadingSkeletonTimerId);
    this.loadingSkeletonTimerId = undefined;
  }

  private remountParticles(): void {
    this.lastParticlesRemountAt = Date.now();
    this.particlesContainer?.destroy();
    this.showParticles = false;
    this.cdr.markForCheck();

    window.setTimeout(() => {
      this.showParticles = true;
      this.cdr.markForCheck();
    }, 0);
  }
}

interface VotingExperienceContext {
  companySuffix: string;
  electionGuid: string;
}
